import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Archive, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Pannello admin per l'archiviazione ordini FATTURATO su Dropbox.
 *
 * - Mostra lo stato dell'ultima esecuzione (automatica o manuale)
 * - Mostra il numero di ordini archiviabili al momento (FATTURATO > 5 giorni)
 * - Permette di lanciare un'archiviazione manuale immediata
 */
export default function ArchiveOrdersCard({ authAxios }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/admin/orders/archive/status");
      setStatus(res.data);
    } catch (err) {
      console.error("Failed to load archive status", err);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleRun = async () => {
    if (!window.confirm("Archiviare ora tutti gli ordini FATTURATO da più di 5 giorni su Dropbox?")) return;
    setRunning(true);
    const tid = toast.loading("Archiviazione in corso...");
    try {
      const res = await authAxios.post("/admin/orders/archive/run");
      const archived = res.data?.archived || 0;
      if (archived === 0) {
        toast.info("Nessun ordine da archiviare", { id: tid });
      } else {
        toast.success(`Archiviati ${archived} ordini su Dropbox`, { id: tid });
      }
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'archiviazione", { id: tid });
    } finally {
      setRunning(false);
    }
  };

  const lastRun = status?.last_run_finished_at;
  const archivableNow = status?.archivable_count_now ?? 0;
  const lastArchivedCount = status?.last_run_archived_count ?? 0;
  const lastStatus = status?.last_run_status;

  return (
    <Card className="mb-6 bg-[#18181b] border-zinc-500/20" data-testid="archive-orders-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Archive className="w-5 h-5 text-zinc-400" />
          Archivio Ordini su Dropbox
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Gli ordini in stato <span className="text-emerald-400 font-medium">FATTURATO</span> da più di 5 giorni vengono
          archiviati automaticamente ogni notte in <span className="font-mono text-zinc-300">/Triolo/Archivio Ordini/YYYY/MM/ordini_YYYY-MM-DD.zip</span>.
          Gli articoli si ricaricano on-demand quando apri il dettaglio.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded-lg bg-black/30 border border-white/5 text-center">
            <p className="text-2xl font-mono font-bold text-amber-400" data-testid="archivable-count">{archivableNow}</p>
            <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Archiviabili adesso</p>
          </div>
          <div className="p-3 rounded-lg bg-black/30 border border-white/5 text-center">
            <p className="text-2xl font-mono font-bold text-emerald-400" data-testid="last-archived-count">{lastArchivedCount}</p>
            <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Ultima esecuzione</p>
          </div>
          <div className="p-3 rounded-lg bg-black/30 border border-white/5 text-center">
            <p className="text-xs font-mono text-zinc-300" data-testid="last-run-time">
              {lastRun ? new Date(lastRun).toLocaleString("it-IT") : "Mai eseguito"}
            </p>
            <p className="text-[11px] text-zinc-500 uppercase tracking-wider mt-1">
              {lastStatus === "ok" && "✓ Ok"}
              {lastStatus === "partial" && "⚠️ Parziale"}
              {lastStatus === "skipped" && "Saltato"}
              {lastStatus === "running" && "In corso..."}
              {!lastStatus && "—"}
            </p>
          </div>
        </div>

        {status?.last_run_message && lastStatus !== "ok" && (
          <p className="text-xs text-amber-300 mb-3" data-testid="last-run-message">
            {status.last_run_message}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="archive-run-btn"
            onClick={handleRun}
            disabled={running || archivableNow === 0}
            className="bg-zinc-700 hover:bg-zinc-600 text-white"
            size="sm"
          >
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Archive className="w-4 h-4 mr-1" />}
            {running ? "Archiviazione..." : `Archivia ora${archivableNow > 0 ? ` (${archivableNow})` : ""}`}
          </Button>
          <Button
            data-testid="archive-refresh-btn"
            onClick={loadStatus}
            disabled={loading}
            variant="ghost"
            size="sm"
            className="text-zinc-400 hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
